import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { translate, TRANSLOCO_SCOPE } from '@ngneat/transloco';
import { Store } from '@ngrx/store';
import { BroadcasterService } from 'ng-broadcaster';
import { BehaviorSubject, Subject } from 'rxjs';
import { delay, filter, takeUntil } from 'rxjs/operators';
import { AddRecordsComponent } from '../addrecords/addrecords.component';
import { AlertError } from '../../store/actions/shared.actions';
import { ApiService } from '../../services/api/api.service';
import { AssetService } from '../../models/assetservice.class';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { Entity } from '../../models/entity.class';
import { GridFieldModalComponent } from '../grid-field-modal/grid-field-modal.component';
import { PaginationInterface } from '../../interface/pagination.interface';
import { Record } from '../../models/record/record.class';
import { RecordField } from '../../models/record/recordfield.class';
import { RecordsPageData } from '../../interface/recordspagedata.interface';
import { TableColumn } from '../../interface/table-column.interface';
import { TableData } from '../../interface/table-data.interface';
import { TableRecordAction } from '../../interface/table-record-action.interface';
import { UtilsService } from '../../services/utils/utils.service';
import { ValidationsService } from '../../services/validations/validations.service';
import { ViewRecordPagination } from '../../interface/viewrecordpagination.interface';
import { ViewRecordType } from '../../interface/viewrecordtype';

@Component({
    selector: 'app-entity-records-display',
    templateUrl: './entity-records-display.component.html',
    styleUrls: ['./entity-records-display.component.scss'],
    providers: [
        {
            provide: TRANSLOCO_SCOPE,
            useValue: '',
        },
    ],
    standalone: false,
})
export class EntityRecordsDisplayComponent implements OnInit, OnChanges, OnDestroy {
    @Input() recordsDataSubject: BehaviorSubject<ViewRecordType>;
    @Input() currentRecordsPagination: BehaviorSubject<ViewRecordPagination>;
    @Input() currentPageData: BehaviorSubject<RecordsPageData[]>;
    @Input() showLogsAction: boolean;
    @Input() inAuditTrailMode = false;
    @Input() enableUpdate: boolean;
    @Input() instanceId: string;
    @Input() fieldsMap: BehaviorSubject<{
        [property: string]: {
            keyColumns: string[];
            valuesMap: {
                id: string;
                masterColumn: string;
                value: any;
                references: any[];
            }[];
        };
    }>;
    @Input() masterEntityFieldValueMap: { [property: string]: string };
    @Input() public instance: any;
    @Input() showCount: boolean;
    @Input() service: AssetService;

    @Output() selectedRecordEvent: EventEmitter<Record> = new EventEmitter();
    @Output() fetchItemsInPage = new EventEmitter();
    @Output() refreshItemsWithNewPageSize = new EventEmitter();
    @Output() fetchRecordLogs: EventEmitter<Record> = new EventEmitter();
    @Output() editColumnEvent = new EventEmitter();
    @Output() refreshEntity = new EventEmitter();
    @Output() selectedRecordsChanged = new EventEmitter<{ entityId: string; recordIds: string[] }>();

    unSubscribe = new Subject<void>();
    allTableColumns: BehaviorSubject<TableColumn[]> = new BehaviorSubject([]);
    selectedColumns: string[] = [];
    tableData: BehaviorSubject<TableData[]> = new BehaviorSubject([]);
    paginationData: BehaviorSubject<PaginationInterface> = new BehaviorSubject({
        count: 0,
        page: 0,
        pageIndex: 0,
    });
    recordActions: TableRecordAction[] = [];
    public selectedRecords: Record[] = [];
    public columnsToUpdate: { [property: string]: boolean } = {};
    public fieldsDisabledForEdit = [];
    public currentEntityId = '';
    public previousEntityId: string;
    public isMultiRecords: boolean;
    public searchRecord: {
        [property: string]: Record;
    } = {};
    public currentEntity: Entity;
    public searchableIndexFields: any[];
    public showError = true;

    constructor(
        protected _api: ApiService,
        private _utilsService: UtilsService,
        private _broadcaster: BroadcasterService,
        private dialog: MatDialog,
        private store$: Store,
        private _validationService: ValidationsService,
        private _commonUtils: CommonUtilsService
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.showLogsAction) {
            this.updateRecordActions();
        }
    }

    updateRecordActions = () => {
        this.recordActions = [];
        const appTags = this.service?.tags || [];
        this.isMultiRecords = appTags?.find(tag => tag.tagKey === 'multi-records') ? true : false;
        if (this.showLogsAction) {
            const action = this.recordActions.find(recordAction => recordAction.displayName === 'Audit Trails');
            if (!action) {
                this.recordActions.push({
                    displayName: translate('Audit Trails'),
                    iconName: 'assignment',
                    onActionTrigger: (data: TableData) => this.fetchRecordLogs.emit(this.getRecordFromTableRecordId(data.recordId)),
                });
                if (this.isMultiRecords) {
                    if (
                        this.instance.value?.instanceState !== 'REQUEST_COMPLETED' &&
                        this.instance.value?.instanceState !== 'REQUEST_CANCELLED'
                    ) {
                        this.recordActions.push({
                            displayName: translate('Edit'),
                            iconName: 'edit',
                            onActionTrigger: (data: TableData) => this.viewRecords(data, 'edit'),
                        });
                    }
                    this.recordActions.push({
                        displayName: translate('View'),
                        iconName: 'remove_red_eye',
                        onActionTrigger: (data: TableData) => this.viewRecords(data, 'view'),
                    });
                }
            }
        }
    };

    public viewRecords = (data: TableData, action) => {
        const parentRecord = this.recordsDataSubject.value.records.find(record => record.id === data.recordId);
        const map =
            parentRecord?.buildMasterEntityFieldValueMap(
                CommonUtilsService.cloneObject(this.fieldsMap.value),
                this.masterEntityFieldValueMap,
                true,
                undefined
            ) || {};
        const record: Record = CommonUtilsService.cloneObject(this.getRecordFromTableRecordId(data.recordId));
        const entity: Entity = CommonUtilsService.cloneObject(this.recordsDataSubject?.value?.entity);
        const pages = this.currentPageData.value;
        const page = pages.find(pageRecord => pageRecord.pageIndex === data.currentPageIndex);
        record.entities = CommonUtilsService.cloneObject(entity.entities);
        entity.entityData = [record];
        record.entities.forEach(ent => {
            this._api.records.getRecords(
                {
                    entity: ent,
                    instanceId: this.instanceId,
                    pagingState: page?.pagingState || '',
                    parentRecordId: record.id,
                    size: page?.pageSize || 10,
                    restApiName: this.service?.restApiName,
                },
                {
                    successCallback: (records, response) => {
                        ent?.entities?.length > 0 &&
                            records.forEach(record => {
                                record.entities = CommonUtilsService.cloneObject(ent.entities);
                            });
                        ent.entityData = records;
                        ent.pagingState = response.pagingState;
                    },
                }
            );
        });
        const dialogRef = this.dialog.open(AddRecordsComponent, {
            panelClass: ['matDialogContainer', 'addRecordsDialog'],
            data: {},
            disableClose: true,
        });
        dialogRef.componentInstance.entities = [entity];
        dialogRef.componentInstance.eventType = action;
        dialogRef.componentInstance.instanceId = this.instanceId;
        dialogRef.componentInstance.instanceAssetId = this.instance?.assetId;
        dialogRef.componentInstance.service = this.service;
        dialogRef.componentInstance.parentId = record.parentId;
        dialogRef.componentInstance.parentEntityUid = entity.uid;
        dialogRef.componentInstance.isClosed =
            this.instance.value.instanceState === 'REQUEST_COMPLETED' || this.instance.value.instanceState === 'CANCELLED';
        dialogRef.componentInstance.parentMasterTablesData = this.buildMastersMapForAddRecords(map);
        dialogRef.afterClosed().subscribe(() => {
            this.refreshEntity.emit();
        });
    };

    public buildMastersMapForAddRecords = (
        map: {
            [property: string]: {
                keyColumns: string[];
                valuesMap: {
                    id: string;
                    masterColumn: string;
                    value: any;
                    references: any[];
                }[];
            };
        } = {}
    ) => {
        const valuesMap = {};
        Object.entries(map).forEach(([tableId, tableMap]) => {
            const keyColumnsString = tableMap.keyColumns.join('|');
            valuesMap[tableId] = valuesMap[tableId] || {};
            valuesMap[tableId][keyColumnsString] = valuesMap[tableId][keyColumnsString] || {
                fieldIdVsColumnId: {},
                masterRecord: {},
            };
            const tableValueMap = valuesMap[tableId][keyColumnsString];
            tableMap.valuesMap.forEach(valueObject => {
                tableValueMap.fieldIdVsColumnId[valueObject.id] = valueObject.masterColumn;
                tableValueMap.masterRecord[valueObject.masterColumn] = valueObject.value;
            });
        });
        return JSON.stringify(valuesMap);
    };

    recordsDataUpdated = (recordsData: ViewRecordType) => {
        this.createTableColumns(recordsData.entity);
        this.updateTableData(recordsData.records);
    };

    private prepareSelectedColumns = fields => {
        this.previousEntityId = this.currentEntityId;
        const showInListFieldNames = [];
        const allUids = [];
        fields.forEach(field => {
            if (field.showInList) {
                showInListFieldNames.push(field.uid);
            }
            allUids.push(field.uid);
        });
        this.selectedColumns = showInListFieldNames.length !== 0 ? showInListFieldNames : allUids.slice(0, 4);
    };

    private createTableColumns = (entity: Entity) => {
        this.fieldsDisabledForEdit = [];
        const allFields = entity.fields.filter(field => field.show);
        allFields.sort(this._utilsService.sortFields);
        (this.previousEntityId !== this.currentEntityId || this.selectedColumns.length === 0) && this.prepareSelectedColumns(allFields);
        const columns: TableColumn[] = [];
        this.searchableIndexFields = [];
        allFields.forEach(field => {
            if (field.datatype === 'ATTACHMENT' || field.isBusinessKey || field.readOnly) {
                this.fieldsDisabledForEdit.push(field.uid);
            }
            if (!columns.some(columnsData => columnsData.id === field.uid)) {
                const columnData: TableColumn = {
                    icon: field.isBusinessKey ? 'vpn_key' : undefined,
                    id: field.uid,
                    name: field.displayName || field.name,
                    options: undefined,
                    type: field.datatype === 'GRID' ? 'GRID' : field.uiTypeMetadata === 'PASSWORD' ? 'PASSWORD' : ('text' as any),
                    hide: !field.show,
                    inputType: field.datatype,
                    uiTypeMetadata: field.uiTypeMetadata,
                };
                field.datatype === 'GRID' && (columnData.callback = (event: TableData) => this.showGridField(field.uid, event));
                columns.push(columnData);
            }
            field.searchableIndex && this.searchableIndexFields.push(field.uid);
        });
        columns.push({
            icon: undefined,
            hide: false,
            hideInAllColumnsList: true,
            id: 'selectRecord',
            name: undefined,
            type: 'SELECTRECORD',
            showCheckboxInHeader: true,
        });
        this.columnsToUpdate = columns.reduce((columns, column) => {
            columns[column.id] = false;
            return columns;
        }, {});
        if (JSON.stringify(columns) !== JSON.stringify(this.allTableColumns.value)) {
            this.allTableColumns.next(columns);
        }
        if (!this.searchRecord[entity.uid]) {
            this.searchRecord[entity.uid] = new Record(undefined, entity);
            this.searchRecord[entity.uid].fields = this.searchRecord[entity.uid].fields.filter(field => field.searchable || field.sortable);
            this.searchRecord[entity.uid].fields.forEach(
                (fld: any) => ((fld.isMandatory = false), this.searchRecord[entity.uid].setFieldValue(fld.id, undefined), (fld.rules = []))
            );
        }
    };

    public onSearch = async () => {
        const isValid = await this._validationService.hasValidData({
            configurations: [],
            entities: this.searchRecord[this.currentEntityId].entities,
            entityId: this.currentEntityId,
            process: undefined,
            record: this.searchRecord[this.currentEntityId],
            restAPIName: this.service?.restApiName,
            inSearch: true,
        });
        if (!isValid) {
            this.store$.dispatch(AlertError({ message: translate('Enter a valid value to proceed') }));
        } else {
            this.refreshItemsWithNewPageSize.emit({
                searchRecordFields: this.searchRecord[this.currentEntityId]?.fields.filter(fld => fld?.value),
                pageSize: this.currentRecordsPagination?.value?.pageSize,
                pageIndex: 0,
                isSearch: true,
            });
        }
    };

    updateTableData = (records: ViewRecordType['records']) => {
        const tableData: TableData[] = [];
        const selectedRecordIds = this.selectedRecords.reduce((ids, record) => {
            ids.push(record.id);
            return ids;
        }, []);
        records?.forEach(record => {
            const allColumns = this.allTableColumns.value;
            let messages: string[] = [];
            if (record.message.errors.length > 0) {
                messages = record.message.errors.slice(0);
            } else if (record.message.warnings.length > 0) {
                messages = record.message.warnings.slice(0);
            }
            const recordData: TableData = {
                recordId: record.id,
                errorsCount: record.errorsCount,
                warningsCount: record.warningsCount,
                tooltip: messages.join('. '),
                inAudit: this.inAuditTrailMode,
                isDeleted: !record.active,
                selected: selectedRecordIds.includes(record.id),
            };
            allColumns.forEach(column => {
                const field = record.getFieldByFieldId(column.id);
                this.setValue(recordData, field, column.id);
            });
            tableData.push(recordData);
        });
        this.tableData.next(tableData);
    };

    setValue = (recordData: TableData, field: RecordField, columnId: string) => {
        if (field) {
            if (this.inAuditTrailMode) {
                this.pushValue(recordData, field, columnId + '_oldValue', field.oldValue);
                this.pushValue(recordData, field, columnId + '_newValue', field.newValue);
            } else {
                this.pushValue(recordData, field, columnId, field.originalValue);
            }
        }
    };

    pushValue = (recordData: TableData, field: RecordField, columnId: string, value: any) => {
        if (field.attachment) {
            const files = [];
            value &&
                Array.isArray(value) &&
                value.forEach(uploadedFileRecordUri => {
                    const file = uploadedFileRecordUri.substring(uploadedFileRecordUri.lastIndexOf('/') + 1, uploadedFileRecordUri.length);
                    files.push(file);
                });
            field.files = files;
            recordData[columnId] = files;
        } else if (field.hasLookup) {
            recordData[columnId] = field.value || '';
        } else if (field.datatype === 'BOOLEAN' && field?.uiTypeMetadata === 'TEXTFIELD') {
            switch (value) {
                case true:
                    recordData[columnId] = 'Yes';
                    break;
                case false:
                    recordData[columnId] = 'No';
                    break;
                default:
                    recordData[columnId] = '';
                    break;
            }
        } else if (field.datatype === 'DATE') {
            recordData[columnId] = value !== undefined ? value : '';
        } else if (['INT', 'LONG', 'DOUBLE', 'DECIMAL', 'FLOAT'].includes(field.datatype)) {
            recordData[columnId] = value !== undefined ? CommonUtilsService.formatNumber(value, field.valueDisplayFormat) : '';
        } else {
            recordData[columnId] = value !== undefined ? value : '';
        }
    };

    protected getRecordFromTableRecordId = (recordId: string): Record => {
        const recordsData = this.recordsDataSubject.value;
        return recordsData.records.find(record => record.id === recordId);
    };

    onRecordSelect = (data: TableData) => {
        const selectedRecord = this.getRecordFromTableRecordId(data.recordId);
        this.selectedRecordEvent.emit(selectedRecord);
    };

    recordSelectedEvent = (data: TableData) => {
        const records = this.recordsDataSubject.value.records;
        records.filter(record => record.id === data.recordId).forEach(record => (record.selected = data.selected));
        const index = this.selectedRecords.findIndex(record => record.id === data.recordId);
        if (data.selected) {
            if (this.selectedRecords.length > 100) {
                return this.store$.dispatch(AlertError({ message: translate('Selection is limited to 100 records') }));
            }
            const record = records.find(recordItem => recordItem.id === data.recordId);
            index === -1 && this.selectedRecords.push(record);
        } else {
            this.selectedRecords.splice(index, 1);
        }
        this.selectedRecordsChanged.emit({
            entityId: this.currentEntityId,
            recordIds: this.selectedRecords.map(r => r.id),
        });
    };

    protected updatePaginationData = (data: ViewRecordPagination) => {
        const recordsData = this.recordsDataSubject.value;
        if (!recordsData) {
            return;
        }
        const recordsSize = recordsData.records && recordsData.records.length;
        const pages = this.currentPageData.value;
        const page = pages.find(pageRecord => pageRecord.pageIndex === data.currentPageIndex);
        let count = 0;
        /* if (recordsData?.entity?.hasSearchableFields && !data.filterTypeSearch?.filterType) {
            count = recordsData?.entity?.pagination?.count;
        } else { */
        if (pages && pages.length > 0) {
            if (page.pageIndex + 1 < pages.length) {
                count = recordsSize + (pages.length - 1 || 0) * (data.pageSize || 0);
            } else {
                count = recordsSize + (data.currentPageIndex || 0) * (data.pageSize || 0) + (recordsSize === data.pageSize ? 1 : 0);
            }
        }
        //}
        const nextPageData = {
            count,
            page: data.currentPageIndex + 1,
            pageIndex: data.currentPageIndex,
            size: data.pageSize,
            searchAfter: page && page.pagingState,
        };
        this.paginationData.next(nextPageData);
    };

    private showGridField = (fieldId: string, rowData: TableData) => {
        const record = this.recordsDataSubject.value?.records?.find(items => items.id === rowData.recordId);
        const field = record?.fields?.find(item => item.id === fieldId);
        if (!field) {
            return;
        }
        const modalRef = this.dialog.open(GridFieldModalComponent, {
            panelClass: ['gridFieldModal', 'matDialogContainer'],
            disableClose: true,
        });
        modalRef.componentInstance.field = field;
        modalRef.componentInstance.readOnly = true;
        modalRef.componentInstance.recordUpdateDisabled = true;
        modalRef.componentInstance.initiateGridData();
    };

    public editColumn = (columnId: string) => {
        const entity = this.recordsDataSubject.value.entity;
        const field = entity.getField(columnId);
        if (field.datatype === 'ATTACHMENT') {
            return this.store$.dispatch(AlertError({ message: translate('Multiple records update is not allowed for attachment fields') }));
        }
        this.editColumnEvent.emit({
            fieldId: columnId,
            entity: entity,
            records: this.selectedRecords,
            clearSelection: this.clearSelection,
        });
    };

    public multiColumnTrigger = () => {
        const entity = this.recordsDataSubject.value.entity;
        let hasAttachmentField = false;
        const columnsToUpdate: string[] = Object.keys(this.columnsToUpdate).reduce((columns, id) => {
            const field = entity.getField(id);
            this.columnsToUpdate[id] && columns.push(field.uid);
            if (this.columnsToUpdate[id] && field?.datatype === 'ATTACHMENT') {
                hasAttachmentField = true;
            }
            return columns;
        }, []);
        if (this.selectedRecords.length === 0) {
            return this.store$.dispatch(AlertError({ message: translate('Select atleast one record') }));
        }
        if (columnsToUpdate.length === 0) {
            return this.store$.dispatch(AlertError({ message: translate('Select atleast one column') }));
        }
        if (hasAttachmentField) {
            return this.store$.dispatch(AlertError({ message: translate('Multiple records update is not allowed for attachment fields') }));
        }
        this.editColumnEvent.emit({
            entity: entity,
            fieldIds: columnsToUpdate,
            records: this.selectedRecords,
            clearSelection: this.clearSelection,
        });
    };

    private clearSelection = () => {
        this.selectedRecords.splice(0);
        this.recordsDataSubject.value.records.forEach(record => {
            record.selected = false;
        });
        this.tableData.value.forEach(record => (record.selected = false));
        Object.keys(this.columnsToUpdate).forEach(id => {
            this.columnsToUpdate[id] = false;
        });
    };

    private reSelectRecords = () => {
        const currentRecords = this.recordsDataSubject.value.records;
        this.selectedRecords = this.selectedRecords.reduce((records, record) => {
            const selectedRecord = currentRecords.find(item => item.id === record.id);
            if (selectedRecord) {
                records.push(selectedRecord);
            } else {
                records.push(record);
            }
            return records;
        }, []);
    };

    ngOnInit() {
        this.recordsDataSubject.value.entity['multiColumnTrigger'] = this.multiColumnTrigger;
        this.recordsDataSubject.value.entity['recordsDeletedEvent'] = this.clearSelection;
        this.recordsDataSubject
            ?.pipe(
                takeUntil(this.unSubscribe),
                filter(data => data !== undefined),
                delay(10)
            )
            .subscribe(data => {
                this.recordsDataSubject.value.entity['multiColumnTrigger'] = this.multiColumnTrigger;
                this.recordsDataSubject.value.entity['recordsDeletedEvent'] = this.clearSelection;
                if (this.currentEntityId !== data.entity?.uid) {
                    this.currentEntity = data.entity;
                    this.currentEntityId = data.entity?.uid;
                    this.selectedRecords.splice(0);
                    this.recordsDataSubject.value.records.forEach(record => {
                        record.selected = false;
                    });
                    this.selectedRecordsChanged.emit({ entityId: '', recordIds: [] });
                } else {
                    this.reSelectRecords();
                }
                this.recordsDataUpdated(data);
                const filterLog = this._commonUtils.getFromStorage('filterColumnLog');
                const session = this._commonUtils.getFromStorage('session');
                if (filterLog && session) {
                    this._broadcaster.broadcast('selectFilterLogColumn');
                }
                if (this.selectedColumns.indexOf('selectRecord') === -1 && !this.inAuditTrailMode) {
                    this.selectedColumns.unshift('selectRecord');
                }
            });
        this.currentPageData
            ?.pipe(
                takeUntil(this.unSubscribe),
                filter(data => data !== undefined),
                delay(200)
            )
            .subscribe(() => {
                this.updatePaginationData(this.currentRecordsPagination.value);
            });
    }

    ngOnDestroy(): void {
        this.unSubscribe.next();
        this.unSubscribe.complete();
        this.tableData.value.forEach(record => (record.selected = false));
    }
}
